import debounce from 'lodash/debounce';
import { getScrollbarWidth } from '@scripts/helpers/utils';
import { modulesLoaded } from '@scripts/helpers/modules-loader';

// CSS custom properties that need resolved pixel values on :root.
// Each is measured on init and resize, then written as --property-px.
const RESOLVED_PROPERTIES = [
	'--fw-container-gutter',
	'--fw-content-container-gutter',
	'--fw-column-gap',
	'--carousel-production-performances-gutter',
	'--layout-padding',
];

/**
 * Sets dynamic CSS custom properties on the root.
 */
class CSSCustomProperties {
	constructor() {
		this.elRoot = document.documentElement;
		this.elHeader = document.querySelector('[data-js-header-primary]');
		this.elMobileUtility = document.querySelector('[data-js-mobile-utility]');
		this.elMeasure = null;
		this.headerObserver = null;
		this.carouselInstances = [];
	}

	init() {
		this._setup();
		this._bindUI();

		if (document.querySelector('[data-js-carousel]')) {
			modulesLoaded.then((instanceLookup) => {
				this.carouselInstances = Array.from(
					instanceLookup
						.getModule('carousel')
						?.values() || []
				);

				this._refreshCarousels();
			});
		}
	}

	_setup() {
		this._updateScrollbarWidth();
		this._updateResolvedProperties();
	}

	_bindUI() {
		this._onWindowResize = debounce(this._onWindowResize.bind(this), 100);

		window.addEventListener('resize', this._onWindowResize);

		if (this.elHeader) {
			this._onHeaderResize = debounce(this._onHeaderResize.bind(this), 50);

			this.headerObserver = new ResizeObserver(this._onHeaderResize);
			this.headerObserver.observe(this.elHeader, { box: 'border-box' });
		}

		if (this.elMobileUtility) {
			this._onMobileUtilityResize = debounce(this._onMobileUtilityResize.bind(this), 50);

			this.mobileUtilityObserver = new ResizeObserver(this._onMobileUtilityResize);
			this.mobileUtilityObserver.observe(this.elMobileUtility, { box: 'border-box' });
		}
	}

	_updateScrollbarWidth() {
		const scrollbarWidth = getScrollbarWidth();

		this.elRoot.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
	}

	/**
	 * Resolve CSS custom properties (including calc()) to pixel values.
	 * Measures a hidden measurement element.
	 */
	_updateResolvedProperties() {
		if (!this.elMeasure) {
			this.elMeasure = document.createElement('div');
			this.elMeasure.style.cssText =
				'position:absolute;visibility:hidden;pointer-events:none';
			document.body.appendChild(this.elMeasure);
		}

		RESOLVED_PROPERTIES.forEach((prop) => {
			this.elMeasure.style.width = `var(${prop})`;
			const px = Math.round(parseFloat(getComputedStyle(this.elMeasure).width) || 0);

			this.elRoot.style.setProperty(`${prop}-px`, `${px}px`);
		});

		this._refreshCarousels();

	}

	/** @param {ResizeObserverEntry[]} entries */
	_onHeaderResize(entries) {
		const entry = entries[0];

		if (!entry.borderBoxSize?.length) return;

		const height = Math.round(entry.borderBoxSize[0].blockSize);

		this.elRoot.style.setProperty('--header-primary-height', `${height}px`);
	}

	/** @param {ResizeObserverEntry[]} entries */
	_onMobileUtilityResize(entries) {
		const entry = entries[0];

		if (!entry.borderBoxSize?.length) return;

		const height = Math.round(entry.borderBoxSize[0].blockSize);

		this.elRoot.style.setProperty('--mobile-utility-height', `${height}px`);
	}

	_onWindowResize() {
		this._updateScrollbarWidth();
		this._updateResolvedProperties();
	}

	_refreshCarousels() {
		this.carouselInstances.forEach((instance) => {
			if (!instance.api) return;
			instance.api.emit('beforeResize');
			instance.api.updateSize();
		});
	}
}

export default function factory() {
	const module = new CSSCustomProperties();

	module.init();

	return module;
}
